import { toIsoBirthDate } from '@/utils/date';
import { mbiIsValid } from '@/utils/mbi';
import { hasStartedWebreg } from '@/utils/webregUserIdGenerator';
import { WREG_STEP_NAME, type WregStep } from '@/components/webreg/wregSteps';

const MBI_MASK = '****-***-****';

type WregIntakeForm = {
  first: string;
  last: string;
  dob: string;
  gender: string;
  mbi: string;
  email: string;
  phone: string;
  tcAccepted: boolean;
};

type WregIntakeTextField = Exclude<keyof WregIntakeForm, 'tcAccepted'>;

type IntakeErrorKey = keyof WregIntakeForm;

const INTAKE_NAME_FIELDS: IntakeErrorKey[] = ['first', 'last', 'tcAccepted'];
const INTAKE_CONTACT_FIELDS: IntakeErrorKey[] = ['phone', 'email'];
const INTAKE_ABOUT_FIELDS: IntakeErrorKey[] = ['dob', 'gender'];
const INTAKE_MEDICARE_FIELDS: IntakeErrorKey[] = ['mbi'];

const FIELD_NAME: Record<IntakeErrorKey, string> = {
  first: 'first_name',
  last: 'last_name',
  dob: 'dob',
  gender: 'gender',
  mbi: 'mbi',
  email: 'email',
  phone: 'phone',
  tcAccepted: 'terms',
};

const INTAKE_STEP_FIELDS: Partial<Record<WregStep, IntakeErrorKey[]>> = {
  intake: INTAKE_NAME_FIELDS,
  'intake-contact': INTAKE_CONTACT_FIELDS,
  'intake-about': INTAKE_ABOUT_FIELDS,
  'intake-mbi': INTAKE_MEDICARE_FIELDS,
};

const intakeFieldNames = (step: WregStep): string[] =>
  (INTAKE_STEP_FIELDS[step] ?? []).map((field) => FIELD_NAME[field]);

const intakeEventProperties = (step: WregStep): Record<string, unknown> => {
  const fields = intakeFieldNames(step);
  return fields.length
    ? { step_name: WREG_STEP_NAME[step], fields, is_retry: hasStartedWebreg() }
    : {};
};

const EMPTY_INTAKE: WregIntakeForm = {
  first: '',
  last: '',
  dob: '',
  gender: '',
  mbi: '',
  email: '',
  phone: '',
  tcAccepted: false,
};

const formatMbi = (value: string): string => {
  const c = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 11);
  return [c.slice(0, 4), c.slice(4, 7), c.slice(7, 11)].filter(Boolean).join('-');
};

const ISO_DOB = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

const formatDob = (value: string): string => {
  const iso = ISO_DOB.exec(value.trim());
  const source = iso ? `${iso[2].padStart(2, '0')}${iso[3].padStart(2, '0')}${iso[1]}` : value;
  let d = source.replace(/\D/g, '').slice(0, 8);
  if (d.length >= 1 && +d[0] > 1) d = `0${d.slice(0, 7)}`;
  if (d.length >= 2 && +d.slice(0, 2) > 12) d = `12${d.slice(2)}`;
  if (d.length >= 3 && +d[2] > 3) d = `${d.slice(0, 2)}0${d.slice(2, 7)}`;
  if (d.length >= 4 && +d.slice(2, 4) > 31) d = `${d.slice(0, 2)}31${d.slice(4)}`;
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
};

const formatPhone = (digits: string): string => {
  if (!digits) return '';
  let o = `(${digits.slice(0, 3)}`;
  if (digits.length >= 3) o += ') ';
  if (digits.length > 3) o += digits.slice(3, 6);
  if (digits.length >= 6) o += '-';
  if (digits.length > 6) o += digits.slice(6, 10);
  return o;
};

const dobIsOutOfRange = (dob: string): boolean => {
  const digits = dob.replace(/\D/g, '');
  if (digits.length !== 8) return false;
  const year = +digits.slice(4);
  return year < 1900 || year > new Date().getFullYear();
};

const emailIsValid = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const dobIsImpossible = (dob: string): boolean =>
  dob.replace(/\D/g, '').length === 8 && !dobIsOutOfRange(dob) && toIsoBirthDate(dob) === '';

const dobIsComplete = (f: WregIntakeForm): boolean => toIsoBirthDate(f.dob) !== '';

const intakeNameIsComplete = (f: WregIntakeForm): boolean =>
  Boolean(f.first.trim() && f.last.trim() && f.tcAccepted);

const intakeContactIsComplete = (f: WregIntakeForm): boolean =>
  Boolean(f.phone.length === 10 && emailIsValid(f.email));

const intakeAboutIsComplete = (f: WregIntakeForm): boolean =>
  Boolean(dobIsComplete(f) && f.gender);

const intakeMedicareIsComplete = (f: WregIntakeForm): boolean => mbiIsValid(f.mbi);

const intakeIsComplete = (f: WregIntakeForm): boolean =>
  intakeNameIsComplete(f) &&
  intakeContactIsComplete(f) &&
  intakeAboutIsComplete(f) &&
  intakeMedicareIsComplete(f);

export {
  EMPTY_INTAKE,
  FIELD_NAME,
  INTAKE_ABOUT_FIELDS,
  INTAKE_CONTACT_FIELDS,
  INTAKE_MEDICARE_FIELDS,
  INTAKE_NAME_FIELDS,
  MBI_MASK,
  dobIsComplete,
  dobIsImpossible,
  dobIsOutOfRange,
  emailIsValid,
  formatDob,
  formatMbi,
  formatPhone,
  intakeAboutIsComplete,
  intakeContactIsComplete,
  intakeEventProperties,
  intakeFieldNames,
  intakeIsComplete,
  intakeMedicareIsComplete,
  intakeNameIsComplete,
};
export type { IntakeErrorKey, WregIntakeForm, WregIntakeTextField };
